'use client';

import { useEffect, useMemo } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { authStore } from '@/infrastructure/store/authStore';
import { permissionsStore } from '@/infrastructure/store/permissionsStore';

import { TPermissionId } from '../constants/permissionIds';
import { isAnyGranted, isGranted, permissionsStatus, TPermissionsStatus } from '../helpers/permissionCheck';
import { routeViewPermissions } from '../helpers/routePermissions';

interface IUsePermissionsResult {
  status: TPermissionsStatus;
  /** The answer arrived (or failed, and the screen fails open). Before it, every gated control stays hidden. */
  ready: boolean;
  can: (id: TPermissionId) => boolean;
  canAny: (ids: ReadonlyArray<TPermissionId>) => boolean;
  /** Whether a route opens for this person; a route without ids opens for everyone. */
  canViewRoute: (route: string) => boolean;
}

/**
 * What the person may see and change, from the answer of `GET /permissions`, which is the same
 * answer the API enforces: a visible button is never one the API refuses. A pure selector: the
 * answer loads once, in `usePermissionsBootstrap`.
 *
 * Brand buyers are not checked against ids (they only reach the Portal, by the e-mail on the
 * brand), so the shell asks this only for factory staff.
 */
export function usePermissions(): IUsePermissionsResult {
  const { answer, failed } = useStore(
    permissionsStore,
    useShallow((s) => ({ answer: s.answer, failed: s.failed })),
  );

  return useMemo(() => {
    const status = permissionsStatus(answer, failed);
    const can = (id: TPermissionId): boolean => isGranted(status, answer, id);
    const canAny = (ids: ReadonlyArray<TPermissionId>): boolean => isAnyGranted(status, answer, ids);
    const canViewRoute = (route: string): boolean => {
      const ids = routeViewPermissions(route);
      return ids === null || canAny(ids);
    };
    return { status, ready: status !== 'pending', can, canAny, canViewRoute };
  }, [answer, failed]);
}

/**
 * Whether the person can only LOOK at what a control writes: none of `editIds` is granted (any
 * one is enough). The screen hides the write controls with it, and says so where a whole form
 * turns read-only. The server still refuses with 403 either way: this only keeps a button that
 * cannot work off the screen.
 */
export function useReadOnly(editIds: TPermissionId | ReadonlyArray<TPermissionId>): boolean {
  const { canAny } = usePermissions();
  const ids: ReadonlyArray<TPermissionId> = typeof editIds === 'string' ? [editIds] : editIds;
  return !canAny(ids);
}

/**
 * Loads the answer ONCE per session and account, from the layout. Gated on the unified login
 * because the layout mounts outside `AuthGuard`, and keyed by the account because the role is
 * per account: the same person may administer one account and only look at another.
 */
export function usePermissionsBootstrap(): void {
  const isAuthenticated = useStore(authStore, (s) => s.isAuthenticated);
  const partnerId = useStore(authStore, (s) => s.user?.partnerId ?? '');
  const load = useStore(permissionsStore, (s) => s.load);

  useEffect(() => {
    if (!isAuthenticated || partnerId === '') {
      return;
    }
    const run = async (): Promise<void> => {
      await load();
    };
    run();
  }, [isAuthenticated, partnerId, load]);
}
